// @flow

import React, {ReactNode, useState} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {Link, withRouter, WithRouterProps} from 'react-router';
import styled from 'styled-components';

import type {LocationDescriptor} from 'history';

interface Props {
  tabs: Array<{
    label: ReactNode;
    name: string;
    content: () => ReactNode;
  }>;
  active?: string;
  contentClass?: string;
}

export const ModernTabs = ({
  tabs,
  active,
  contentClass,
}: Props): JSX.Element => {
  const firstTabName = tabs[0].name;
  let activeContent: () => ReactNode = () => null;

  const [activeTab, setActive] = useState(active || firstTabName);

  return (
    <TabsStructure
      tabs={tabs}
      tabBuilder={({label, name, content}) => {
        let active = name === activeTab;
        if (active) activeContent = content;

        return (
          <button
            key={name}
            className={cs('tabs__item', {active})}
            onClick={() => setActive(name)}
          >
            <span>{label}</span>
          </button>
        );
      }}
      content={() => activeContent()}
      contentClass={contentClass}
    />
  );
};

interface TabsStructureProps<T> {
  tabs: Array<T>;
  tabBuilder: (tab: T, index: number) => JSX.Element;
  content: () => ReactNode;
  contentClass?: string;
}

const TabsStructure = <T,>({
  tabs,
  content,
  tabBuilder,
  contentClass,
}: TabsStructureProps<T>): JSX.Element => (
  <StyledContainerDiv tabsCount={tabs.length}>
    <div className="tabs d-flex flex-wrap">{tabs.map(tabBuilder)}</div>
    <div className={cs('tabs-content', contentClass)}>{content()}</div>
  </StyledContainerDiv>
);

interface LinkProps extends WithRouterProps {
  tabs: Array<{
    label: ReactNode;
    to: LocationDescriptor;
    content: () => ReactNode;
  }>;
  contentClass?: string;
}

export const ModernLinkTabs = withRouter(
  ({tabs, router, contentClass}: LinkProps) => {
    let activeContent: () => ReactNode = () => null;

    return (
      <TabsStructure
        tabs={tabs}
        tabBuilder={({label, to, content}, index) => {
          const active = router.isActive(to);
          if (active) activeContent = content;

          return (
            <Link className={cs('tabs__item', {active})} key={index} to={to}>
              <span>{label}</span>
            </Link>
          );
        }}
        content={() => activeContent()}
        contentClass={contentClass}
      />
    );
  }
);

const StyledContainerDiv = styled.div<{tabsCount: number}>`
  > .tabs {
    border-bottom: 1px solid ${colors.borderGray};

    > .tabs__item {
      color: ${colors.textDefault};
      background: none;
      border: none;
      padding: 10px 0.5rem 5px;
      margin: 0 10px;
      transition: all 0.1s ease-in-out;
      border-bottom: 4px solid #fff;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      flex-basis: 100%;

      @media (min-width: 576px) {
        flex-basis: calc(
          ${({tabsCount}) => (tabsCount % 3 === 0 ? '33' : '50')}% - 20px
        );
      }

      @media (min-width: 768px) {
        flex-basis: auto;

        &:first-of-type {
          margin-left: 0;
        }
      }

      > span {
        transition: all 0.1s ease-in-out;
        opacity: 0.8;
      }

      &:focus {
        outline: none;
      }

      &:hover {
        text-decoration: none;
        border-bottom: 4px solid ${colors.lightGray};

        > span {
          opacity: 1;
        }
      }

      &.active {
        border-bottom: 4px solid ${colors.selectedBg};

        > span {
          opacity: 1;
        }
      }

      @media (max-width: 575px) {
        &.active {
          color: #fff;
          background: ${colors.selectedBg};
          border-radius: 4px;
        }
      }
    }
  }
`;
